import { useState, useEffect, useCallback } from 'react';

import type { JobResultResponse } from '../../../../../packing/services/packing-service';

type AppliedStateEntry = {
  isApplied: boolean;
  packageName: string;
};

type UsePackingAppliedReturn = {
  isPackingApplied: boolean;
  isCalculating: boolean;
  packageName: string | null;
};

type PackingAppliedDetail = {
  fulfillmentPlanId: string;
  packageName: string;
};

type AppliedListener = (fpId: string, packageName: string | null) => void;
type CalculateListener = (fpId: string, isRerun: boolean) => void;
type CalculatingListener = (fpId: string, isCalculating: boolean) => void;

type ActiveJob = {
  jobId: string;
  startTime: number;
};

const MAX_APPLIED_ENTRIES = 100;
const appliedState = new Map<string, AppliedStateEntry>();
const calculatingState = new Set<string>();
const errorState = new Set<string>();
const activeJobs = new Map<string, ActiveJob>();
const resultCache = new Map<string, JobResultResponse>();
const appliedListeners = new Set<AppliedListener>();
const calculateListeners = new Set<CalculateListener>();
const calculatingListeners = new Set<CalculatingListener>();

const notifyApplied = (fpId: string, packageName: string | null): void => {
  appliedListeners.forEach((listener) => listener(fpId, packageName));
};

const notifyCalculating = (fpId: string, isCalculating: boolean): void => {
  calculatingListeners.forEach((listener) => listener(fpId, isCalculating));
};

export const getAppliedState = (fulfillmentPlanId: string): AppliedStateEntry | undefined => appliedState.get(fulfillmentPlanId);

export const onAppliedChange = (listener: AppliedListener): (() => void) => {
  appliedListeners.add(listener);
  return () => {
    appliedListeners.delete(listener);
  };
};

export const isCurrentlyCalculating = (fulfillmentPlanId: string): boolean => calculatingState.has(fulfillmentPlanId);

export const setActiveJob = (fulfillmentPlanId: string, jobId: string, startTime: number): void => {
  if (activeJobs.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = activeJobs.keys().next().value;
    if (oldestKey) activeJobs.delete(oldestKey);
  }
  activeJobs.set(fulfillmentPlanId, { jobId, startTime });
};

export const getActiveJob = (fulfillmentPlanId: string): ActiveJob | null => activeJobs.get(fulfillmentPlanId) ?? null;

export const markError = (fulfillmentPlanId: string): void => {
  if (errorState.size >= MAX_APPLIED_ENTRIES) {
    const oldest = errorState.values().next().value;
    if (oldest) errorState.delete(oldest);
  }
  errorState.add(fulfillmentPlanId);
};

export const hasError = (fulfillmentPlanId: string): boolean => errorState.has(fulfillmentPlanId);

export const clearError = (fulfillmentPlanId: string): void => {
  errorState.delete(fulfillmentPlanId);
};

export const cacheResult = (fulfillmentPlanId: string, result: JobResultResponse): void => {
  if (resultCache.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = resultCache.keys().next().value;
    if (oldestKey) resultCache.delete(oldestKey);
  }
  resultCache.set(fulfillmentPlanId, result);
};

export const getCachedResult = (fulfillmentPlanId: string): JobResultResponse | null => resultCache.get(fulfillmentPlanId) ?? null;

export const clearCachedResult = (fulfillmentPlanId: string): void => {
  resultCache.delete(fulfillmentPlanId);
};

export const markPackingApplied = ({ fulfillmentPlanId, packageName }: PackingAppliedDetail): void => {
  if (appliedState.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = appliedState.keys().next().value;
    if (oldestKey) appliedState.delete(oldestKey);
  }
  appliedState.set(fulfillmentPlanId, { isApplied: true, packageName });
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  resultCache.delete(fulfillmentPlanId);
  notifyApplied(fulfillmentPlanId, packageName);
  notifyCalculating(fulfillmentPlanId, false);
};

export const resetPackingApplied = (fulfillmentPlanId: string): void => {
  appliedState.delete(fulfillmentPlanId);
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  resultCache.delete(fulfillmentPlanId);
  errorState.delete(fulfillmentPlanId);
  notifyApplied(fulfillmentPlanId, null);
  notifyCalculating(fulfillmentPlanId, false);
};

export const markCalculating = (fulfillmentPlanId: string): void => {
  calculatingState.add(fulfillmentPlanId);
  notifyCalculating(fulfillmentPlanId, true);
};

export const clearCalculating = (fulfillmentPlanId: string): void => {
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  errorState.delete(fulfillmentPlanId);
  notifyCalculating(fulfillmentPlanId, false);
};

export const triggerCalculation = (fulfillmentPlanId: string, isRerun = false): void => {
  if (calculatingState.has(fulfillmentPlanId)) return;
  if (isRerun) {
    resetPackingApplied(fulfillmentPlanId);
  }
  markCalculating(fulfillmentPlanId);
  calculateListeners.forEach((listener) => listener(fulfillmentPlanId, isRerun));
  if (calculateListeners.size === 0) {
    clearCalculating(fulfillmentPlanId);
  }
};

export const useOnTriggerCalculation = (fulfillmentPlanId: string, callback: (isRerun: boolean) => void): void => {
  const handleTrigger = useCallback(
    (fpId: string, isRerun: boolean) => {
      if (fpId === fulfillmentPlanId) callback(isRerun);
    },
    [fulfillmentPlanId, callback],
  );

  useEffect(() => {
    calculateListeners.add(handleTrigger);
    return () => {
      calculateListeners.delete(handleTrigger);
    };
  }, [handleTrigger]);
};

export const usePackingApplied = (fulfillmentPlanId: string): UsePackingAppliedReturn => {
  const persistedState = appliedState.get(fulfillmentPlanId);
  const [isPackingApplied, setIsPackingApplied] = useState(persistedState?.isApplied ?? false);
  const [packageName, setPackageName] = useState<string | null>(persistedState?.packageName ?? null);
  const [isCalculating, setIsCalculating] = useState(calculatingState.has(fulfillmentPlanId));

  const handleChange = useCallback(
    (fpId: string, name: string | null) => {
      if (fpId !== fulfillmentPlanId) return;
      setIsPackingApplied(name !== null);
      setPackageName(name);
    },
    [fulfillmentPlanId],
  );

  const handleCalculatingChange = useCallback(
    (fpId: string, calculating: boolean) => {
      if (fpId !== fulfillmentPlanId) return;
      setIsCalculating(calculating);
    },
    [fulfillmentPlanId],
  );

  useEffect(() => {
    appliedListeners.add(handleChange);
    return () => {
      appliedListeners.delete(handleChange);
    };
  }, [handleChange]);

  useEffect(() => {
    calculatingListeners.add(handleCalculatingChange);
    return () => {
      calculatingListeners.delete(handleCalculatingChange);
    };
  }, [handleCalculatingChange]);

  return { isPackingApplied, isCalculating, packageName };
};
